/**
 * campaignTimingUtils.js
 *
 * Core domain algorithms for campaign frequency capping, business hours,
 * quiet hours, timezone conversion, and automated
 * next-eligible-period rescheduling.
 */

import { formatLocalDate, calculateDiffDays, addDays } from './dateUtils';
import { lookupTimezoneByPhone, lookupTimezoneByCountry } from './globalCountryTimezones';

// Default standard timing configuration
export const DEFAULT_TIMING_CONFIG = {
  // Business Days: Array of 1-7 where 1 = Monday, 7 = Sunday
  businessDays: [1, 2, 3, 4, 5], // Monday - Friday
  sendWindowStart: '09:00', // 9:00 AM (HH:MM 24h format)
  sendWindowEnd: '18:00', // 6:00 PM (HH:MM 24h format)
  timezoneMode: 'FIXED', // Enforce fixed user/campaign timezone
  fixedTimezone: 'Asia/Kuala_Lumpur', // Default workspace timezone
  quietHoursEnabled: true,
  quietHoursStart: '18:00', // 6:00 PM
  quietHoursEnd: '09:00', // 9:00 AM next day
  excludeWeekends: true,
  maxTouchesPerPeriod: 3, // Max automated touches
  periodDays: 7, // within a 7-day rolling window
  minDelayHours: 48, // Minimum delay between touches (e.g. 48h)
  maxCampaignTouches: 5, // Maximum total touches per recipient
};

// Timing presets for quick configuration
export const TIMING_PRESETS = [
  {
    id: 'standard_b2b',
    label: 'Standard B2B (Mon–Fri 9–6)',
    description: 'Mon–Fri 09:00–18:00, 3 touches/7d, 48h min delay',
    config: {
      businessDays: [1, 2, 3, 4, 5],
      sendWindowStart: '09:00',
      sendWindowEnd: '18:00',
      quietHoursEnabled: true,
      quietHoursStart: '18:00',
      quietHoursEnd: '09:00',
      excludeWeekends: true,
      maxTouchesPerPeriod: 3,
      periodDays: 7,
      minDelayHours: 48,
      maxCampaignTouches: 5,
    },
  },
  {
    id: 'aggressive_fast',
    label: 'Fast Follow-up (High Urgency)',
    description: 'Mon–Fri 08:30–18:30, 4 touches/7d, 24h min delay',
    config: {
      businessDays: [1, 2, 3, 4, 5],
      sendWindowStart: '08:30',
      sendWindowEnd: '18:30',
      quietHoursEnabled: true,
      quietHoursStart: '18:30',
      quietHoursEnd: '08:30',
      excludeWeekends: true,
      maxTouchesPerPeriod: 4,
      periodDays: 7,
      minDelayHours: 24,
      maxCampaignTouches: 6,
    },
  },
  {
    id: 'conservative_csuite',
    label: 'Executive / C-Suite (Paced)',
    description: 'Tue–Thu 10:00–16:00, 2 touches/7d, 72h min delay',
    config: {
      businessDays: [2, 3, 4], // Tuesday, Wednesday, Thursday
      sendWindowStart: '10:00',
      sendWindowEnd: '16:00',
      quietHoursEnabled: true,
      quietHoursStart: '16:00',
      quietHoursEnd: '10:00',
      excludeWeekends: true,
      maxTouchesPerPeriod: 2,
      periodDays: 7,
      minDelayHours: 72,
      maxCampaignTouches: 4,
    },
  },
];

// Common timezones supported for selection & reference
export const COMMON_TIMEZONES = [
  { id: 'Asia/Kuala_Lumpur', label: 'Asia/Kuala_Lumpur (MYT / UTC+8)' },
  { id: 'Asia/Singapore', label: 'Asia/Singapore (SGT / UTC+8)' },
  { id: 'Asia/Jakarta', label: 'Asia/Jakarta (WIB / UTC+7)' },
  { id: 'Asia/Bangkok', label: 'Asia/Bangkok (ICT / UTC+7)' },
  { id: 'Asia/Manila', label: 'Asia/Manila (PHT / UTC+8)' },
  { id: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (HKT / UTC+8)' },
  { id: 'Asia/Tokyo', label: 'Asia/Tokyo (JST / UTC+9)' },
  { id: 'Australia/Sydney', label: 'Australia/Sydney (AEST / UTC+10)' },
  { id: 'Europe/London', label: 'Europe/London (GMT/BST / UTC+0/+1)' },
  { id: 'America/New_York', label: 'America/New_York (EST/EDT / UTC-5/-4)' },
  { id: 'America/Chicago', label: 'America/Chicago (CST/CDT / UTC-6/-5)' },
  { id: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT / UTC-8/-7)' },
  { id: 'UTC', label: 'UTC (Coordinated Universal Time)' },
];

/**
 * Resolve prospect timezone from available metadata (timezone, phone dialing code, country, or location).
 */
export function resolveProspectTimezone(prospect, defaultTz = 'Asia/Kuala_Lumpur') {
  if (!prospect) return defaultTz;

  if (prospect.timezone && typeof prospect.timezone === 'string' && prospect.timezone.trim()) {
    return prospect.timezone.trim();
  }

  const phone = String(prospect.phone || prospect.phone_number || '').trim();
  if (phone) {
    const tzFromPhone = lookupTimezoneByPhone(phone);
    if (tzFromPhone) return tzFromPhone;
  }

  const countryCandidate = prospect.country || prospect.location || prospect.country_code || '';
  if (countryCandidate) {
    const tzFromCountry = lookupTimezoneByCountry(String(countryCandidate));
    if (tzFromCountry) return tzFromCountry;
  }

  return defaultTz;
}

/**
 * Get date parts (year, month, day, hour, minute, dayOfWeek 1=Mon..7=Sun) in a given IANA timezone.
 */
export function getDatePartsInTimezone(date, timezone) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) {
    return null;
  }

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(d);
    let year = 2026;
    let month = 1;
    let day = 1;
    let weekdayStr = 'Mon';
    let hour = 0;
    let minute = 0;

    parts.forEach((p) => {
      if (p.type === 'year') year = parseInt(p.value, 10);
      if (p.type === 'month') month = parseInt(p.value, 10);
      if (p.type === 'day') day = parseInt(p.value, 10);
      if (p.weekday !== undefined || p.type === 'weekday') weekdayStr = p.value;
      if (p.type === 'hour') hour = parseInt(p.value, 10);
      if (p.type === 'minute') minute = parseInt(p.value, 10);
    });

    const weekdayMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
    const dayOfWeek = weekdayMap[weekdayStr] || 1;
    const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const timeMinutes = hour * 60 + minute;

    return {
      year,
      month,
      day,
      dayOfWeek,
      weekdayStr,
      hour,
      minute,
      timeMinutes,
      dateKey,
    };
  } catch (err) {
    const dayOfWeek = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
      dayOfWeek,
      weekdayStr: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][dayOfWeek - 1],
      hour: d.getUTCHours(),
      minute: d.getUTCMinutes(),
      timeMinutes: d.getUTCHours() * 60 + d.getUTCMinutes(),
      dateKey: d.toISOString().split('T')[0],
    };
  }
}

/**
 * Parse HH:MM into minutes from start of day (0..1439).
 */
export function parseTimeToMinutes(timeStr = '09:00') {
  const [h, m] = String(timeStr)
    .split(':')
    .map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
}

/**
 * Format minutes into 12-hour AM/PM string.
 */
export function formatMinutesToAmPm(minutes) {
  const h24 = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const suffix = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * Check if a given date object falls within allowed business days, hours, and not on a weekend.
 */
export function isInsideSendingWindow(date, timezone, config = DEFAULT_TIMING_CONFIG) {
  const cfg = { ...DEFAULT_TIMING_CONFIG, ...config };
  const parts = getDatePartsInTimezone(date, timezone);
  if (!parts) return { inside: false, reason: 'Invalid date or timezone' };

  // Weekend check
  if (cfg.excludeWeekends && (parts.dayOfWeek === 6 || parts.dayOfWeek === 7)) {
    return {
      inside: false,
      reason: `Weekend (${parts.weekdayStr})`,
      parts,
    };
  }

  // Business day check
  if (Array.isArray(cfg.businessDays) && !cfg.businessDays.includes(parts.dayOfWeek)) {
    return {
      inside: false,
      reason: `Non-business day (${parts.weekdayStr})`,
      parts,
    };
  }

  // Business hours check
  const startMin = parseTimeToMinutes(cfg.sendWindowStart);
  const endMin = parseTimeToMinutes(cfg.sendWindowEnd);

  if (parts.timeMinutes < startMin) {
    return {
      inside: false,
      reason: `Before business hours (${formatMinutesToAmPm(parts.timeMinutes)} < ${formatMinutesToAmPm(startMin)})`,
      parts,
    };
  }

  if (parts.timeMinutes >= endMin) {
    return {
      inside: false,
      reason: `After business hours (${formatMinutesToAmPm(parts.timeMinutes)} >= ${formatMinutesToAmPm(endMin)})`,
      parts,
    };
  }

  // Quiet hours check
  if (cfg.quietHoursEnabled) {
    const qStart = parseTimeToMinutes(cfg.quietHoursStart);
    const qEnd = parseTimeToMinutes(cfg.quietHoursEnd);
    const inQuietHours =
      qStart > qEnd
        ? parts.timeMinutes >= qStart || parts.timeMinutes < qEnd
        : parts.timeMinutes >= qStart && parts.timeMinutes < qEnd;

    if (inQuietHours) {
      return {
        inside: false,
        reason: `Inside quiet hours (${formatMinutesToAmPm(qStart)} - ${formatMinutesToAmPm(qEnd)})`,
        parts,
      };
    }
  }

  return { inside: true, reason: 'Allowed sending window', parts };
}

/**
 * Given a target time and configuration, calculate the Next Eligible Sending Time.
 * If targetTime already falls inside the allowed window, returns targetTime without modification.
 * Otherwise, advances day-by-day and sets time to sendWindowStart until all constraints are met.
 */
export function calculateNextEligibleSendTime({
  targetTime,
  prospectTimezone = 'Asia/Kuala_Lumpur',
  config = DEFAULT_TIMING_CONFIG,
  pastTouches = [],
}) {
  const cfg = { ...DEFAULT_TIMING_CONFIG, ...config };
  let current = new Date(targetTime);
  if (isNaN(current.getTime())) {
    current = new Date();
  }

  let rescheduled = false;
  let reasons = [];

  // Check max campaign touches total first
  if (
    cfg.maxCampaignTouches > 0 &&
    Array.isArray(pastTouches) &&
    pastTouches.length >= cfg.maxCampaignTouches
  ) {
    return {
      nextEligibleTime: null,
      rescheduled: false,
      blocked: true,
      reason: `Maximum campaign touch limit reached (${cfg.maxCampaignTouches} touches max)`,
      reasons: [`Maximum campaign touch limit reached (${cfg.maxCampaignTouches} touches max)`],
      primaryReason: `Maximum campaign touch limit reached (${cfg.maxCampaignTouches} touches max)`,
      timezone: prospectTimezone,
      formattedLocal: null,
    };
  }

  // Ensure minimum delay from last touch if applicable
  if (Array.isArray(pastTouches) && pastTouches.length > 0 && cfg.minDelayHours > 0) {
    const lastTouchTime = Math.max(
      ...pastTouches
        .map((t) => new Date(t.sentAt || t.date || t).getTime())
        .filter((ms) => !isNaN(ms))
    );
    if (lastTouchTime && !isNaN(lastTouchTime)) {
      const minEligibleTimestamp = lastTouchTime + cfg.minDelayHours * 3600 * 1000;
      if (current.getTime() < minEligibleTimestamp) {
        current = new Date(minEligibleTimestamp);
        rescheduled = true;
        reasons.push(`Minimum delay of ${cfg.minDelayHours}h between touches required`);
      }
    }
  }

  // Cap loop iterations to avoid infinite loops
  const maxIterations = 30 * 24;
  let iterations = 0;

  const startMin = parseTimeToMinutes(cfg.sendWindowStart);

  while (iterations < maxIterations) {
    iterations++;
    const status = isInsideSendingWindow(current, prospectTimezone, cfg);

    if (status.inside) {
      // Check frequency caps in trailing window (e.g. 3 touches / 7 days)
      if (
        cfg.maxTouchesPerPeriod > 0 &&
        cfg.periodDays > 0 &&
        Array.isArray(pastTouches) &&
        pastTouches.length >= cfg.maxTouchesPerPeriod
      ) {
        const windowMs = cfg.periodDays * 86400 * 1000;
        const windowStartMs = current.getTime() - windowMs;
        const touchesInWindow = pastTouches.filter((t) => {
          const tMs = new Date(t.sentAt || t.date || t).getTime();
          return tMs >= windowStartMs && tMs <= current.getTime();
        });

        if (touchesInWindow.length >= cfg.maxTouchesPerPeriod) {
          rescheduled = true;
          reasons.push(
            `Frequency cap (${cfg.maxTouchesPerPeriod} touches in ${cfg.periodDays} days)`
          );
          const earliestTouchMs = Math.min(
            ...touchesInWindow.map((t) => new Date(t.sentAt || t.date || t).getTime())
          );
          const earliestFreeMs = earliestTouchMs + windowMs + 60 * 1000;
          current = new Date(Math.max(current.getTime() + 86400 * 1000, earliestFreeMs));
          continue;
        }
      }

      // Check max campaign touches total
      if (
        cfg.maxCampaignTouches > 0 &&
        Array.isArray(pastTouches) &&
        pastTouches.length >= cfg.maxCampaignTouches
      ) {
        return {
          nextEligibleTime: null,
          rescheduled: true,
          blocked: true,
          reason: `Maximum campaign touch limit reached (${cfg.maxCampaignTouches} touches max)`,
          timezone: prospectTimezone,
        };
      }

      return {
        nextEligibleTime: current,
        rescheduled,
        reasons: Array.from(new Set(reasons)),
        primaryReason: reasons[0] || 'Scheduled on-time',
        timezone: prospectTimezone,
        formattedLocal: formatLocalDateTimeInTz(current, prospectTimezone),
      };
    }

    rescheduled = true;
    if (!reasons.includes(status.reason)) {
      reasons.push(status.reason);
    }

    const parts = status.parts || getDatePartsInTimezone(current, prospectTimezone);

    // If on an allowed day and time is before business hours, advance to sendWindowStart today
    const isAllowedDay =
      (!cfg.excludeWeekends || (parts.dayOfWeek !== 6 && parts.dayOfWeek !== 7)) &&
      (!Array.isArray(cfg.businessDays) || cfg.businessDays.includes(parts.dayOfWeek));

    if (isAllowedDay && parts.timeMinutes < startMin) {
      const diffMs = (startMin - parts.timeMinutes) * 60 * 1000;
      current = new Date(current.getTime() + diffMs);
      continue;
    }

    // Otherwise advance to next calendar day at sendWindowStart
    const minutesToNextDayStart = 1440 - parts.timeMinutes + startMin;
    current = new Date(current.getTime() + minutesToNextDayStart * 60 * 1000);
  }

  return {
    nextEligibleTime: current,
    rescheduled: true,
    reasons,
    primaryReason: reasons[0] || 'Rescheduled to next window',
    timezone: prospectTimezone,
    formattedLocal: formatLocalDateTimeInTz(current, prospectTimezone),
  };
}

/**
 * Format date in prospect timezone as readable string: "Mon, 18 May 2026, 09:00 AM".
 */
export function formatLocalDateTimeInTz(date, timezone) {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(date));
  } catch {
    return new Date(date).toLocaleString();
  }
}

/**
 * Simulate the full campaign cadence timeline for a given start date, journey, and config.
 */
export function simulateCadenceSchedule({
  startDate = formatLocalDate(new Date()),
  endDate = null,
  nominalHour = '10:00',
  journey = [],
  config = DEFAULT_TIMING_CONFIG,
  prospectTimezone = 'Asia/Kuala_Lumpur',
}) {
  const cfg = { ...DEFAULT_TIMING_CONFIG, ...config };
  const baseStart = new Date(`${startDate}T${nominalHour}:00`);
  const recordedTouches = [];

  return (journey || []).map((step) => {
    let nominalDate;
    if (step.nominalDate) {
      const timePart = step.nominalTime || nominalHour || '09:00';
      const formattedTime = timePart.length === 5 ? `${timePart}:00` : timePart;
      nominalDate = new Date(`${step.nominalDate}T${formattedTime}`);
    } else {
      const dayOffset = Math.max(0, (parseInt(step.day, 10) || 1) - 1);
      nominalDate = new Date(baseStart.getTime() + dayOffset * 86400 * 1000);
    }

    const timingResult = calculateNextEligibleSendTime({
      targetTime: nominalDate,
      prospectTimezone,
      config: cfg,
      pastTouches: recordedTouches,
    });

    if (timingResult.nextEligibleTime) {
      recordedTouches.push(timingResult.nextEligibleTime);
    }

    const isDifferent =
      timingResult.rescheduled &&
      timingResult.nextEligibleTime &&
      Math.abs(timingResult.nextEligibleTime.getTime() - nominalDate.getTime()) > 60 * 1000;

    const actualParts = timingResult.nextEligibleTime
      ? getDatePartsInTimezone(timingResult.nextEligibleTime, prospectTimezone)
      : null;
    const actualDateKey = actualParts ? actualParts.dateKey : null;
    const isBeyondCampaignEnd = Boolean(endDate && actualDateKey && actualDateKey > endDate);

    const reasons = [...(timingResult.reasons || [])];
    if (isBeyondCampaignEnd) {
      reasons.push(`Exceeds campaign end date (${endDate})`);
    }

    const primaryReason = isBeyondCampaignEnd
      ? `Exceeds campaign end date (${endDate})`
      : timingResult.blocked
        ? timingResult.reason || 'Blocked by timing guardrails'
        : isDifferent
          ? timingResult.primaryReason
          : 'On-Schedule Window';

    return {
      stepNumber: step.step,
      stepName: step.name || `Touchpoint ${step.step}`,
      channel: step.channel,
      condition: step.condition,
      nominalDay: step.day,
      nominalDate,
      nominalFormatted: formatLocalDateTimeInTz(nominalDate, prospectTimezone),
      actualDate: timingResult.nextEligibleTime,
      actualFormatted: timingResult.formattedLocal,
      isRescheduled: isDifferent,
      isBlocked: Boolean(timingResult.blocked),
      isBeyondCampaignEnd,
      reason: primaryReason,
      reasons: reasons.length > 0 ? reasons : ['On-Schedule Window'],
      timezone: prospectTimezone,
    };
  });
}

/**
 * Parse a date string (YYYY-MM-DD) and time string (HH:MM) in a specified IANA timezone
 * into an exact UTC Date object.
 */
export function parseDateTimeInTimezone(
  dateStr,
  timeStr = '09:00',
  timezone = 'Asia/Kuala_Lumpur'
) {
  if (!dateStr || typeof dateStr !== 'string') return new Date();
  const timeFormatted = timeStr && timeStr.length === 5 ? `${timeStr}:00` : timeStr || '09:00:00';
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm, ss] = timeFormatted.split(':').map(Number);

  if (isNaN(y) || isNaN(m) || isNaN(d)) return new Date();

  // Initial naive guess in UTC
  const guessUtc = Date.UTC(y, m - 1, d, hh || 0, mm || 0, ss || 0);

  // Determine timezone representation when evaluating guessUtc in timezone
  const parts = getDatePartsInTimezone(new Date(guessUtc), timezone);
  if (!parts) return new Date(guessUtc);

  const tzGuessUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0);
  const offsetMs = tzGuessUtc - guessUtc;

  return new Date(guessUtc - offsetMs);
}

/**
 * Dynamically resolves the timezone abbreviation (e.g. 'BST', 'GMT', 'EDT', 'EST', 'JST', 'MYT', 'CET').
 * Uses Intl.DateTimeFormat with DST awareness across all global timezones.
 * Gracefully handles non-alphabetical or offset representations.
 */
export function getTimezoneAbbreviation(date = new Date(), timezone = 'Asia/Kuala_Lumpur') {
  try {
    const d = date instanceof Date ? date : new Date(date);
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(d);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    const rawVal = tzPart?.value;

    if (rawVal && /^[A-Za-z]{2,5}$/.test(rawVal)) {
      return rawVal;
    }

    // Standard business aliases for locales where stripped ICU outputs "GMT+X":
    if (timezone === 'Asia/Kuala_Lumpur') return 'MYT';
    if (timezone === 'Asia/Singapore') return 'SGT';
    if (timezone === 'Asia/Tokyo') return 'JST';
    if (timezone === 'Asia/Hong_Kong') return 'HKT';
    if (timezone === 'Asia/Jakarta') return 'WIB';
    if (timezone === 'Asia/Bangkok') return 'ICT';
    if (timezone === 'Asia/Manila') return 'PHT';
    if (timezone === 'UTC') return 'UTC';

    if (rawVal) return rawVal;

    return timezone.split('/').pop()?.replace('_', ' ') || timezone;
  } catch {
    return timezone.split('/').pop()?.replace('_', ' ') || timezone;
  }
}

/**
 * Resolves the effective timezone for the campaign.
 * All scheduling and dispatches operate on the user's/campaign's fixed timezone.
 */
export function resolveEffectiveTimezone({ prospect, config = DEFAULT_TIMING_CONFIG } = {}) {
  const cfg = { ...DEFAULT_TIMING_CONFIG, ...(config || {}) };
  return cfg.fixedTimezone || 'Asia/Kuala_Lumpur';
}

/**
 * Strictly aligns a campaign journey sequence to the campaign's business days, weekend rules,
 * and minimum delays. Ensures NO step can ever land on a disallowed day (e.g. Saturday or Sunday),
 * whether generated by AI or by default.
 */
export function alignJourneyToTimingGuardrails({
  journey = [],
  startDate = formatLocalDate(new Date()),
  endDate = null,
  config = DEFAULT_TIMING_CONFIG,
  timezone = 'Asia/Kuala_Lumpur',
  filterToDuration = false,
}) {
  if (!Array.isArray(journey) || journey.length === 0) return [];
  const cfg = { ...DEFAULT_TIMING_CONFIG, ...config };
  const targetTz = timezone || cfg.fixedTimezone || 'Asia/Kuala_Lumpur';
  const sendTime = cfg.sendWindowStart || '09:00';

  const pastTouches = [];
  let previousDateStr = startDate;

  const aligned = journey.map((step, idx) => {
    let candidateDt;
    if (idx === 0) {
      candidateDt = parseDateTimeInTimezone(startDate, sendTime, targetTz);
    } else {
      const prevStep = journey[idx - 1];
      const stepDay = Number(step.day) || idx + 1;
      const prevDay = Number(prevStep.day) || idx;
      const dayDiff = Math.max(1, stepDay - prevDay);

      const minHours = Math.max(cfg.minDelayHours || 48, dayDiff * 24);
      const prevDt =
        pastTouches[idx - 1] || parseDateTimeInTimezone(previousDateStr, sendTime, targetTz);
      candidateDt = new Date(prevDt.getTime() + minHours * 3600 * 1000);
    }

    const eligible = calculateNextEligibleSendTime({
      targetTime: candidateDt,
      prospectTimezone: targetTz,
      config: cfg,
      pastTouches,
    });

    const eligibleDt = eligible.nextEligibleTime || candidateDt;
    pastTouches.push(eligibleDt);

    const parts = getDatePartsInTimezone(eligibleDt, targetTz);
    const dateStr = parts ? parts.dateKey : formatLocalDate(eligibleDt);
    const timeStr = sendTime;
    previousDateStr = dateStr;

    const nominalDay = calculateDiffDays(startDate, dateStr) + 1;
    const isExceeding = Boolean(endDate && dateStr > endDate);

    return {
      ...step,
      step: idx + 1,
      day: Math.max(1, nominalDay),
      nominalDate: dateStr,
      nominalTime: timeStr,
      timezone: targetTz,
      isExceedingDuration: isExceeding,
    };
  });

  let result = aligned;
  if (filterToDuration && endDate) {
    const withinDuration = aligned.filter((s) => !s.isExceedingDuration);
    // If all steps exceeded (e.g. extreme calendar restriction), keep at least the initial touch clamped to startDate
    if (withinDuration.length === 0 && aligned.length > 0) {
      result = [
        {
          ...aligned[0],
          step: 1,
          nominalDate: startDate,
          day: 1,
          isExceedingDuration: false,
        },
      ];
    } else {
      result = withinDuration.map((s, idx) => ({
        ...s,
        step: idx + 1,
      }));
    }
  }

  const maxAllowedTouches = Number(cfg.maxCampaignTouches);
  if (maxAllowedTouches > 0 && result.length > maxAllowedTouches) {
    result = result.slice(0, maxAllowedTouches).map((s, idx) => ({
      ...s,
      step: idx + 1,
    }));
  }

  return result;
}

/**
 * Get current user/browser local timezone and formatted time.
 */
export function getUserLocalTimezoneInfo(date = new Date()) {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kuala_Lumpur';
    const abbr = getTimezoneAbbreviation(date, tz);
    const formattedTime = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);

    return {
      timezone: tz,
      abbreviation: abbr,
      formattedTime,
    };
  } catch {
    return {
      timezone: 'Asia/Kuala_Lumpur',
      abbreviation: 'MYT',
      formattedTime: '',
    };
  }
}

/**
 * Calculates the exact scheduled UTC timestamp for a specific lead on a step.
 * In PROSPECT_LOCAL mode, calculates when stepDate + stepTime occurs in that lead's local timezone.
 * In FIXED mode, calculates when stepDate + stepTime occurs in fixedTimezone.
 */
export function getLeadScheduledTimestamp({
  step,
  lead,
  config = DEFAULT_TIMING_CONFIG,
  startDate = formatLocalDate(new Date()),
}) {
  if (!step) return null;
  const cfg = { ...DEFAULT_TIMING_CONFIG, ...(config || {}) };
  const targetTz = resolveEffectiveTimezone({ prospect: lead, config: cfg });
  const stepTime = step.nominalTime || cfg.sendWindowStart || '09:00';
  let stepDate = step.nominalDate;
  if (!stepDate) {
    const dayOffset = Math.max(0, (parseInt(step.day, 10) || 1) - 1);
    stepDate = addDays(startDate, dayOffset) || startDate;
  }
  const dt = parseDateTimeInTimezone(stepDate, stepTime, targetTz);
  return {
    timestamp: dt.getTime(),
    date: dt,
    timezone: targetTz,
    stepDate,
    stepTime,
  };
}

/**
 * Resolves the timezone summary for an active lead cohort.
 * Standardized to the campaign/user's fixed timezone.
 */
export function resolveCohortTimezoneSummary(leads = [], config = DEFAULT_TIMING_CONFIG) {
  const cfg = { ...DEFAULT_TIMING_CONFIG, ...(config || {}) };
  const fixedTz = cfg.fixedTimezone || 'Asia/Kuala_Lumpur';
  const abbr = getTimezoneAbbreviation(new Date(), fixedTz);
  return {
    mode: 'FIXED',
    isUniform: true,
    timezone: fixedTz,
    abbreviation: abbr,
    badgeText: abbr,
    description: `Campaign Timezone: ${fixedTz}`,
  };
}
